import React from 'react';
import { EquipmentItem, EquipmentSlots } from './equipmentTypes';

interface SlotProps {
  slotName: string;
  label: string;
  item: EquipmentItem | null;
  onUnequip: (slot: string) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent, slot: string) => void;
}

const EquipmentSlot: React.FC<SlotProps> = ({ slotName, label, item, onUnequip, onDragOver, onDrop }) => {
  const isEmpty = !item;
  return (
    <div
      className={`equipment-slot slot-${slotName} ${isEmpty ? 'empty' : 'filled'}`}
      onDragOver={onDragOver}
      onDrop={(e) => onDrop(e, slotName)}
      onClick={() => !isEmpty && onUnequip(slotName)}
      title={isEmpty ? `Empty ${label} slot` : `Click to unequip ${item.name}`}
    >
      <div className="slot-label">{label}</div>
      {isEmpty ? (
        <div className="empty-slot-icon">📦</div>
      ) : (
        <div className="equipped-item">
          <div className="item-name">{item.name}</div>
          {item.isTwoHanded && <div className="two-handed-badge">⚔️ 2H</div>}
          <div className="item-stats">
            {item.attackBonus > 0 && (
              <div className="stat-pill"><span className="stat-pill-label">ATK</span><span className="stat-pill-value">+{item.attackBonus}</span></div>
            )}
            {item.defenseBonus > 0 && (
              <div className="stat-pill"><span className="stat-pill-label">DEF</span><span className="stat-pill-value">+{item.defenseBonus}</span></div>
            )}
            {item.hpBonus > 0 && (
              <div className="stat-pill"><span className="stat-pill-label">HP</span><span className="stat-pill-value">+{item.hpBonus}</span></div>
            )}
            {item.speedBonus > 0 && (
              <div className="stat-pill"><span className="stat-pill-label">SPD</span><span className="stat-pill-value">+{item.speedBonus}</span></div>
            )}
          </div>
          {(item.minDamage || item.maxDamage) && (
            <div className="weapon-stats" style={{ fontSize: '0.85em', marginTop: '4px', color: '#FFB74D' }}>
              <div>⚔️ {item.minDamage || 0}-{item.maxDamage || 0} {item.damageType || 'Physical'}</div>
              {(item.minRange || item.maxRange) && (
                <div>📏 Range: {item.minRange || 1}-{item.maxRange || 1}</div>
              )}
              {item.weaponAPCost && item.weaponAPCost > 1 && (
                <div>⚡ {item.weaponAPCost} AP</div>
              )}
              {item.aoeShape && item.aoeShape !== 'Single' && (
                <div style={{ color: '#FF5722' }}>💥 {item.aoeShape}</div>
              )}
              {item.usesAmmo && (
                <div style={{ color: item.currentAmmo === 0 ? '#F44336' : '#FFC107' }}>
                  🎯 {item.currentAmmo || 0}/{item.maxAmmo || 0} ammo
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface EquipmentSlotGridProps {
  equipment: EquipmentSlots;
  onUnequip: (slot: string) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent, slot: string) => void;
}

const EquipmentSlotGrid: React.FC<EquipmentSlotGridProps> = ({ equipment, onUnequip, onDragOver, onDrop }) => {
  const s = (name: string, label: string, item: EquipmentItem | null) => (
    <EquipmentSlot key={name} slotName={name} label={label} item={item} onUnequip={onUnequip} onDragOver={onDragOver} onDrop={onDrop} />
  );

  return (
    <div className="paper-doll">
      <h3>Equipment Slots</h3>
      <div className="equipment-grid">
        {s('head',     'Head',      equipment.head)}
        {s('necklace', 'Necklace',  equipment.necklace)}
        {s('mainHand', 'Main Hand', equipment.mainHand)}
        {s('chest',    'Chest',     equipment.chest)}
        {s('offHand',  'Off Hand',  equipment.offHand)}
        {s('hands',    'Hands',     equipment.hands)}
        {s('ring1',    'Ring 1',    equipment.ring1)}
        {s('belt',     'Belt',      equipment.belt)}
        {s('ring2',    'Ring 2',    equipment.ring2)}
        {s('back',     'Back',      equipment.back)}
        {s('legs',     'Legs',      equipment.legs)}
        {s('feet',     'Feet',      equipment.feet)}
      </div>
    </div>
  );
};

export default EquipmentSlotGrid;
